package com.htmlagent.site.state

import com.htmlagent.shared.auth.MeResponse
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.w3c.dom.get
import org.w3c.dom.set

/**
 * What the browser keeps about who is here, which is no longer who they are.
 *
 * The identity lives in an `HttpOnly` session cookie the server sets and this code cannot read;
 * the screen learns it from `/api/auth/me`. It is shown as one string: the email of an account,
 * or the `guest-…` key of a guest.
 *
 * What stays here is convenience: the last session open, and the identity an older version of
 * the site kept in `localStorage`, read once to carry it over. Reads go through `runCatching`
 * because storage throws in private mode or with cookies blocked.
 */
object Identity {

    /** Where older versions kept the identity. Only read to migrate it, then deleted. */
    private const val LEGACY_KEY = "html-agent.user"
    private const val SESSION_KEY = "html-agent.session"

    /** Marks a guest key. Part of the value, as on the server, not a separate flag. */
    private const val GUEST_PREFIX = "guest-"

    /** Query parameter that restores a guest on another browser. See [recoveryLink]. */
    const val RECOVERY_PARAM = "clave"

    /** The string the screen shows for whoever is signed in. */
    fun of(me: MeResponse): String? = me.email ?: me.guestKey

    /** An address may start with `guest-` too; the `@` is what no guest key has. */
    fun isGuest(value: String): Boolean = value.startsWith(GUEST_PREFIX) && '@' !in value

    /**
     * The link that resumes a guest elsewhere. The key travels in it, so the link is the
     * credential: it is offered to be saved, never shown as something to share.
     */
    fun recoveryLink(key: String): String = "${window.location.origin}/app?$RECOVERY_PARAM=$key"

    /** The identity an older version stored, if any: a guest key to resume, or an address to prefill. */
    fun legacy(): String? = runCatching { localStorage[LEGACY_KEY] }.getOrNull()?.trim()?.takeIf { it.isNotBlank() }

    fun forgetLegacy() {
        runCatching { localStorage.removeItem(LEGACY_KEY) }
    }

    /** Leaving: nothing of the previous person is left for the next one to open. */
    fun forget() {
        forgetLegacy()
        forgetSession()
    }

    /**
     * The conversation that was open last time, so it can be reopened instead of creating an
     * empty session on every reload.
     */
    fun lastSession(): String? = runCatching { localStorage[SESSION_KEY] }.getOrNull()
        ?.takeIf { it.isNotBlank() }

    fun rememberSession(id: String) {
        runCatching { localStorage[SESSION_KEY] = id }
    }

    fun forgetSession() {
        runCatching { localStorage.removeItem(SESSION_KEY) }
    }

    /**
     * A minimal check to help with typing, not a validation nor a defense: it keeps a clumsy
     * finger from asking for a link to an address that cannot exist. The server checks again.
     */
    fun looksLikeEmail(value: String): Boolean =
        Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]{2,}$").matches(value.trim())
}
